/**
 * Script hygiene, the streaming chunker, the model's structured reply, the
 * game's memory of recurring jokes, and the framed stream a vs-computer game
 * reads (after Single Didge's public/shared/readout.js). Pure: no network, no
 * DOM; runs in the Worker, the Durable Object, the browser and the tests.
 */

export const MAX_SCRIPT_CHARS = 640; // hard cap for a hole readout (the target is per player count, prompt.ts)
export const MAX_FINAL_SCRIPT_CHARS = 1500; // the presentation, about a minute at Tibbs's pace

/**
 * Delivery cues (TIBBS-VOICE.md 3.5). The prompt no longer asks for any (Tibbs is
 * read straight through, fast), but scripts stored before that still carry
 * them, so they stay known. Anything else in square brackets is stripped
 * before it is spoken, so a player name cannot smuggle a cue in and the model
 * cannot invent one. worker/readout.ts turns these into Cartesia controls.
 */
export const CUES = ['beat', 'pause', 'long pause', 'dry', 'warm', 'proud', 'wistful', 'amazed'] as const;
export type Cue = (typeof CUES)[number];

/** "-12" or "−12" read as "minus 12": the voice must never say "dash". */
export function speakMinus(text: string): string {
  return String(text ?? '').replace(/(^|[\s(,:;"'])[-−–](\d)/g, '$1minus $2');
}

/** Only spoken words and known cues survive, and the length stays under the budget. */
export function sanitizeScript(text: string, maxChars = 0): string {
  const allowed = new Set<string>(CUES);
  let s = speakMinus(text)
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^\s*#+.*$/gm, '')
    .replace(/\*\*|__|^\s*[-*]\s+/gm, '')
    .replace(/\[([^\]]*)\]/g, (_m, inner: string) => (allowed.has(inner.trim().toLowerCase()) ? `[${inner.trim().toLowerCase()}]` : ' '))
    .replace(/<[^>]*>/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .trim();
  if (maxChars && s.length > maxChars) {
    const cut = s.slice(0, maxChars);
    const end = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '), cut.lastIndexOf('\n'));
    s = (end > maxChars * 0.6 ? cut.slice(0, end + 1) : cut).trim();
  }
  return s;
}

/** A script as a caption: no cues, tidy spaces. */
export function captionText(text: string): string {
  return String(text ?? '')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?;:])/g, '$1')
    .trim();
}

// ---------------------------------------------------------------------------
// The presentation's frame: it always opens and closes with the same words.

export const FINAL_OPENER = 'Members and guests. Welcome to the presentation of the Golden Amphora.';
export const FINAL_CLOSER = 'Festina lente.';
const HOLD_TAIL_CHARS = 120; // the streamer keeps this much unspoken so the closer can be normalized
const LEADING_CUES = /^(?:\s*\[[^\]]*\])*\s*/;
const OPENER_LINE = /^(?:members and guests\b[^.!?\n]*[.!?]*\s*|welcome\b[^.!?\n]*(?:presentation|amphora|tour)[^.!?\n]*[.!?]*\s*)/i;

/** The text with the model's own opener removed, and any cues that led it. */
export function splitOpener(text: string): { lead: string; rest: string } {
  const s = String(text ?? '').trimStart();
  const m = s.match(LEADING_CUES)?.[0] ?? '';
  const lead = m.trim();
  let rest = s.slice(m.length);
  for (let i = 0; i < 3; i++) {
    const o = rest.match(OPENER_LINE);
    if (!o) break;
    rest = rest.slice(o[0].length);
  }
  return { lead, rest };
}

/** The text with the model's own closer removed, and the cue that led it. */
export function splitCloser(text: string): { text: string; cue: string } {
  let s = String(text ?? '').trimEnd();
  let cue = '';
  for (let i = 0; i < 4; i++) {
    const dangling = s.match(/(?:\s*\[[^\]]*\])+\s*$/);
    const m = s.match(/(?:(\[[^\]]*\])\s*)?\bfestina lente\b[.!…]*\s*$/i);
    if (m && m.index !== undefined && (m.index === 0 || /[\s.!?\]]/.test(s[m.index - 1]!))) {
      cue = m[1] ?? cue;
      s = s.slice(0, m.index).trimEnd();
      continue;
    }
    if (dangling && dangling.index !== undefined && dangling.index + dangling[0].length >= s.length) {
      cue = dangling[0].trim().split(/\s+(?=\[)/).at(-1) ?? '';
      s = s.slice(0, dangling.index).trimEnd();
      continue;
    }
    break;
  }
  return { text: s, cue };
}

const cued = (cue: string, line: string) => (cue ? `${cue} ${line}` : line);
export const openerBlock = (lead = '') => cued(lead, FINAL_OPENER);
export const closerBlock = (cue = '') => cued(cue, FINAL_CLOSER);

/** A final script, sanitized, starting with the exact opener and ending with the exact closer, within maxChars. */
export function frameFinalScript(text: string, maxChars = MAX_FINAL_SCRIPT_CHARS): string {
  const { lead, rest } = splitOpener(sanitizeScript(text, 0));
  const { text: body, cue } = splitCloser(rest);
  const open = openerBlock(lead);
  const close = closerBlock(cue);
  const room = Math.max(0, maxChars - open.length - close.length - 2);
  const trimmed = sanitizeScript(body, room);
  return [open, trimmed, close].filter(Boolean).join(trimmed ? '\n' : ' ');
}

// ---------------------------------------------------------------------------
// Chunks: TTS requests of a few hundred characters, cut at sentence ends.

/**
 * Split a script into balanced chunks of at most maxChars, at sentence or line
 * boundaries, never inside a [cue]. A cue that would end a chunk moves to the
 * start of the next (cues color the words after them).
 */
export function chunkScript(script: string, maxChars: number): string[] {
  const units: string[] = [];
  for (const raw of String(script).split(/(?<=[.!?])\s+|\n+/)) {
    const u = raw.trim();
    if (!u) continue;
    if (u.length <= maxChars) {
      units.push(u);
      continue;
    }
    let cur = '';
    for (const w of u.match(/\[[^\]]*\]|\S+/g) ?? []) {
      if (cur && cur.length + w.length + 1 > maxChars) {
        units.push(cur);
        cur = '';
      }
      cur = cur ? `${cur} ${w}` : w;
    }
    if (cur) units.push(cur);
  }
  for (let i = 0; i < units.length - 1; i++) {
    const m = units[i]!.match(/\s*(\[[^\]]*\])$/);
    if (m && units[i]!.length > m[0].length) {
      units[i] = units[i]!.slice(0, -m[0].length).trim();
      units[i + 1] = `${m[1]} ${units[i + 1]}`;
    }
  }
  const total = units.reduce((n, u) => n + u.length + 1, 0);
  const count = Math.max(1, Math.ceil(total / maxChars));
  const target = total / count;
  const chunks: string[] = [];
  let cur = '';
  for (const u of units) {
    const left = count - chunks.length;
    if (cur && (cur.length + u.length + 1 > maxChars || (left > 1 && cur.length + 1 >= target))) {
      chunks.push(cur);
      cur = '';
    }
    cur = cur ? `${cur} ${u}` : u;
  }
  if (cur) chunks.push(cur);
  return chunks;
}

// ---------------------------------------------------------------------------
// The model's reply: the spoken script, then a delimiter and a JSON trailer with the running gags.

export const READOUT_JSON_DELIM = '===READOUT-JSON===';

function tryJson(s: string): Record<string, unknown> | null {
  const t = String(s ?? '')
    .replace(/```(?:json)?/gi, '')
    .trim();
  if (!t) return null;
  const parse = (x: string) => {
    try {
      const v: unknown = JSON.parse(x);
      return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  };
  const whole = parse(t);
  if (whole) return whole;
  const a = t.indexOf('{');
  const b = t.lastIndexOf('}');
  return a >= 0 && b > a ? parse(t.slice(a, b + 1)) : null;
}

/** A bare JSON object holding gags at the end of the text, without the delimiter. */
export function splitTrailingJson(raw: string): { text: string; trailer: string } {
  const s = String(raw ?? '');
  let i = s.indexOf('{');
  for (let tries = 0; i >= 0 && tries < 20; tries++) {
    const obj = tryJson(s.slice(i));
    if (obj && obj.gags) return { text: s.slice(0, i), trailer: s.slice(i) };
    i = s.indexOf('{', i + 1);
  }
  return { text: s, trailer: '' };
}

export interface ParsedReply {
  script: string;
  gags: Record<string, unknown>;
}

/** Split model output into { script, gags }. Robust to a missing delimiter, code fences and junk. */
export function parseModelOutput(text: string, { maxChars = MAX_FINAL_SCRIPT_CHARS, final = false } = {}): ParsedReply {
  const finish = (t: string) => (final ? frameFinalScript(t, maxChars) : sanitizeScript(t, maxChars));
  let raw = String(text ?? '');
  let trailer = '';
  const di = raw.indexOf(READOUT_JSON_DELIM);
  if (di >= 0) {
    trailer = raw.slice(di + READOUT_JSON_DELIM.length);
    raw = raw.slice(0, di);
  } else {
    const whole = tryJson(raw);
    if (whole && typeof whole.script === 'string') return { script: finish(whole.script), gags: (whole.gags as Record<string, unknown>) ?? {} };
    ({ text: raw, trailer } = splitTrailingJson(raw));
  }
  const obj = tryJson(trailer) ?? {};
  const gags = obj.gags && typeof obj.gags === 'object' && !Array.isArray(obj.gags) ? (obj.gags as Record<string, unknown>) : {};
  return { script: finish(raw), gags };
}

/**
 * Cuts a streaming script into speakable chunks as the text arrives, so speech
 * can start before the model has finished. A chunk closes at a sentence end
 * once it holds about `soft` characters (or at a space past `hard`). The JSON
 * trailer is held back, never spoken. `framed` (the presentation) normalizes
 * the opener and closer.
 */
export class ScriptStreamer {
  private framed: boolean;
  private headDone: boolean;
  private soft: number;
  private hard: number;
  private maxChars: number;
  private buf = '';
  chunks: string[] = [];
  trailer = '';
  private inTrailer = false;
  private spoken = 0;
  private capped = false;

  constructor({ soft = 360, hard = 700, maxChars = MAX_FINAL_SCRIPT_CHARS, framed = false } = {}) {
    this.framed = framed;
    this.headDone = !framed;
    this.soft = soft;
    this.hard = hard;
    this.maxChars = maxChars;
  }

  /** Feed a text delta. Returns the chunks it closed (usually none or one). */
  push(delta: string): string[] {
    if (this.inTrailer) {
      this.trailer += delta;
      return [];
    }
    this.buf += delta;
    const di = this.buf.indexOf(READOUT_JSON_DELIM);
    if (di >= 0) {
      this.trailer = this.buf.slice(di + READOUT_JSON_DELIM.length);
      this.buf = this.buf.slice(0, di);
      this.inTrailer = true;
      return this.flush();
    }
    const out: string[] = [];
    if (!this.headDone && this.buf.length >= 200) this.head();
    if (!this.headDone) return out;
    for (;;) {
      const cut = this.cutPoint();
      if (cut < 0) break;
      const c = this.emit(this.buf.slice(0, cut));
      this.buf = this.buf.slice(cut).replace(/^\s+/, '');
      if (c) out.push(c);
    }
    return out;
  }

  /** No more text: closes the last chunk. */
  end(): string[] {
    if (this.inTrailer) return [];
    const { text, trailer } = splitTrailingJson(this.buf);
    if (trailer) {
      this.trailer = trailer;
      this.buf = text;
    }
    this.inTrailer = true;
    return this.flush();
  }

  get script(): string {
    return this.chunks.join('\n');
  }

  private flush(): string[] {
    if (!this.framed) {
      const c = this.emit(this.buf);
      this.buf = '';
      return c ? [c] : [];
    }
    if (!this.headDone) this.head();
    const { text, cue } = splitCloser(this.buf);
    this.buf = '';
    const closer = closerBlock(cue);
    const c = this.emit(text);
    if (c) {
      this.chunks[this.chunks.length - 1] = `${c} ${closer}`;
      this.spoken += closer.length + 1;
      return [this.chunks.at(-1)!];
    }
    this.chunks.push(closer);
    this.spoken += closer.length + 1;
    return [closer];
  }

  private head(): void {
    const { lead, rest } = splitOpener(this.buf);
    this.buf = `${openerBlock(lead)} ${rest}`;
    this.headDone = true;
  }

  private emit(text: string): string | null {
    if (this.capped) return null;
    let s = sanitizeScript(text, 0);
    if (!s) return null;
    const limit = this.framed ? this.maxChars - FINAL_CLOSER.length - 10 : this.maxChars;
    if (this.spoken + s.length > limit) {
      s = sanitizeScript(s, Math.max(0, limit - this.spoken));
      this.capped = true;
      if (!s) return null;
    }
    this.spoken += s.length + 1;
    this.chunks.push(s);
    return s;
  }

  private cutPoint(): number {
    const b = this.buf;
    if (b.length < this.soft) return -1;
    const room = this.framed ? b.length - HOLD_TAIL_CHARS : Infinity;
    let best = -1;
    const re = /[.!?]["')]?(?=\s)|\n/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(b))) {
      const end = m.index + m[0].length;
      if (end >= this.soft && end <= this.hard && end <= room) best = end;
      if (end > this.hard) break;
    }
    if (best > 0) return best;
    if (b.length >= this.hard) {
      const sp = b.lastIndexOf(' ', this.hard);
      const open = b.lastIndexOf('[', sp);
      const close = b.lastIndexOf(']', sp);
      if (sp > 0 && sp <= room && !(open > close)) return sp;
    }
    return -1;
  }
}

// ---------------------------------------------------------------------------
// The game's memory of recurring jokes (Single Didge's motifs): spotted in a
// finished script by regex, so a model-written line counts too, and carried
// as a list of ids. Each motif has a per-game allowance.

export interface Motif {
  id: string;
  label: string;
  re: RegExp;
  /** Times allowed in one game. */
  max: number;
}

/**
 * The ancient-world misfortunes Tibbs roasts a bad score with (TIBBS-VOICE.md 1.3),
 * each with the few words of context that let a listener who never studied
 * history get the joke. Spotted by regex in any script and used once a game.
 */
export const MISFORTUNES: { id: string; text: string; re: RegExp }[] = [
  { id: 'mf-delphi', text: 'breathing the same fumes as the Oracle at Delphi, the priestess who sat over a crack in the rock breathing volcanic vapors and seeing visions', re: /delphi|oracle/i },
  { id: 'mf-sisyphus', text: 'Sisyphus, doomed to push a boulder up a hill forever, only for it to roll back down', re: /sisyphus|boulder/i },
  { id: 'mf-icarus', text: 'Icarus, who flew too close to the sun on wings made of wax', re: /icarus|wings (?:made )?of wax|wax wings/i },
  { id: 'mf-trojan', text: 'the Trojans, who wheeled a giant wooden horse full of enemy soldiers into their own city because it looked like a gift', re: /trojans?|wooden horse/i },
  { id: 'mf-lions', text: 'thrown to the lions in the arena', re: /\blions?\b/i },
  { id: 'mf-chariot', text: 'a chariot wreck at the races in Rome, where the crashes were what the crowd paid to see', re: /chariot/i },
  { id: 'mf-desert', text: 'wandering the desert with an empty water skin, seeing mirages', re: /desert|mirage|water ?skin/i },
  { id: 'mf-locusts', text: 'a plague of locusts', re: /locusts?/i },
  { id: 'mf-medusa', text: 'Medusa, the monster whose stare turns people to stone', re: /medusa|turn(?:s|ed)? (?:\w+ )?to stone/i },
  { id: 'mf-minotaur', text: 'the Minotaur\'s maze, a labyrinth with a bull-headed monster in the middle', re: /minotaur|labyrinth/i },
  { id: 'mf-sirens', text: 'the Sirens, who sang so sweetly that sailors steered straight onto the rocks', re: /\bsirens?\b/i },
  { id: 'mf-pandora', text: 'Pandora, who opened the one box she was told never to open and let every trouble out into the world', re: /pandora/i },
  { id: 'mf-midas', text: 'King Midas, who turned everything he touched to gold, including his lunch', re: /midas/i },
  { id: 'mf-tantalus', text: 'Tantalus, stuck forever with the water and the fruit just out of reach', re: /tantalus/i },
  { id: 'mf-galley', text: 'chained to an oar on a Roman galley', re: /galley|\boars?\b/i },
  { id: 'mf-stables', text: 'the Augean stables, thirty years of manure that Hercules had one day to clean', re: /augean|manure/i },
  { id: 'mf-odysseus', text: 'Odysseus, who took ten years to find his way home', re: /odysseus|ulysses/i },
  { id: 'mf-hydra', text: 'the Hydra, the monster that grew two heads for every one cut off', re: /hydra/i },
  { id: 'mf-cyclops', text: 'the Cyclops, the one-eyed giant who kept sailors in his cave for dinner', re: /cyclops/i },
];

export const MOTIFS: Motif[] = [
  { id: 'adorable', label: '"Adorable."', re: /\badorable\b/i, max: 1 },
  { id: 'quaint', label: '"Quaint."', re: /\bquaint\b/i, max: 1 },
  { id: 'suppose', label: '"I suppose"', re: /\bI suppose\b/i, max: 2 },
  { id: 'told', label: '"I\'m told"', re: /\bI(?:'m| am) told\b/i, max: 2 },
  { id: 'lovely', label: '"Lovely."', re: /\blovely\b/i, max: 2 },
  { id: 'naturally', label: '"Naturally."', re: /\bnaturally\b/i, max: 2 },
  { id: 'if-you-like', label: '"If you like that sort of thing"', re: /if you like that sort of thing/i, max: 1 },
  { id: 'so-close', label: '"So close to mattering"', re: /close to mattering/i, max: 1 },
  { id: 'festina', label: '"Festina lente" in a hole readout (it belongs to the ceremony)', re: /festina lente/i, max: 1 },
  ...MISFORTUNES.map((s) => ({ id: s.id, label: s.text.split(',')[0]!, re: s.re, max: 1 })),
];
const MOTIF_IDS = new Set(MOTIFS.map((m) => m.id));
export const MAX_USED_MOTIFS = 96;

/** Motif ids a script uses (each once per script). */
export function motifsUsedIn(text: string): string[] {
  const t = String(text ?? '');
  return MOTIFS.filter((m) => m.re.test(t)).map((m) => m.id);
}

/** A known-ids-only motif list, newest last, capped. Repeats are kept: some motifs are allowed twice. */
export function cleanMotifs(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  return list
    .map((x) => String(x ?? ''))
    .filter((id) => MOTIF_IDS.has(id))
    .slice(-MAX_USED_MOTIFS);
}

/** Motifs whose allowance is used up this game. */
export function exhaustedMotifs(used: readonly string[]): Motif[] {
  const count = new Map<string, number>();
  for (const id of used) count.set(id, (count.get(id) ?? 0) + 1);
  return MOTIFS.filter((m) => (count.get(m.id) ?? 0) >= m.max);
}

// ---------------------------------------------------------------------------
// While Tibbs is summoned, the button rotates through these.

export const LOADING_MESSAGES = [
  'Tibbs is clearing his throat…',
  'Tibbs is sighing at the scorecard…',
  'Tibbs is looking for something nice to say…',
  'Tibbs is adjusting his cap…',
  'Tibbs is checking the math. Twice…',
  'Tibbs is pretending not to be impressed…',
  'Tibbs is counting the points…',
  'Tibbs is choosing his words. Carefully…',
];
export const LOADING_PERIOD_MS = 2200;

/** Which loading message to show `elapsedMs` into a wait (static under reduced motion). */
export function loadingMessageAt(elapsedMs: number, { period = LOADING_PERIOD_MS, offset = 0, reducedMotion = false, messages = LOADING_MESSAGES } = {}): string {
  const n = messages.length;
  const start = ((offset % n) + n) % n;
  if (reducedMotion) return messages[start]!;
  const step = Math.max(0, Math.floor((Number(elapsedMs) || 0) / period));
  return messages[(start + step) % n]!;
}

// ---------------------------------------------------------------------------
// The framed stream a vs-computer game reads while its readout is being made:
//   [type u8][length u32 big-endian][payload]
// 1 = an MP3 chunk, 2 = JSON meta (the script is complete), 3 = JSON end,
// 4 = JSON error, 5 = JSON text of the chunk that follows (captions).

export const FRAME = { AUDIO: 1, META: 2, END: 3, ERROR: 4, TEXT: 5 } as const;
export const FRAMES_TYPE = 'application/x-readout-frames';

export function encodeFrame(type: number, payload: Uint8Array | unknown): Uint8Array {
  const body = payload instanceof Uint8Array ? payload : new TextEncoder().encode(JSON.stringify(payload ?? null));
  const out = new Uint8Array(5 + body.length);
  out[0] = type;
  new DataView(out.buffer).setUint32(1, body.length);
  out.set(body, 5);
  return out;
}

export type Frame = { type: 1; bytes: Uint8Array } | { type: 2 | 3 | 4 | 5; data: Record<string, unknown> | null };

/** Reassembles frames from arbitrary network chunks. */
export class FrameDecoder {
  private buf = new Uint8Array(0);

  push(chunk: Uint8Array): Frame[] {
    const merged = new Uint8Array(this.buf.length + chunk.length);
    merged.set(this.buf);
    merged.set(chunk, this.buf.length);
    this.buf = merged;
    const out: Frame[] = [];
    while (this.buf.length >= 5) {
      const len = new DataView(this.buf.buffer, this.buf.byteOffset).getUint32(1);
      if (this.buf.length < 5 + len) break;
      const type = this.buf[0]!;
      const body = this.buf.slice(5, 5 + len);
      this.buf = this.buf.slice(5 + len);
      if (type === FRAME.AUDIO) out.push({ type: 1, bytes: body });
      else if (type >= 2 && type <= 5) {
        let data: Record<string, unknown> | null = null;
        try {
          data = JSON.parse(new TextDecoder().decode(body)) as Record<string, unknown>;
        } catch {
          data = null;
        }
        out.push({ type: type as 2 | 3 | 4 | 5, data });
      }
    }
    return out;
  }
}
